import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "custom-toast-header" }
const _hoisted_3 = { class: "custom-toast-title" }
const _hoisted_4 = { class: "title-text" }
const _hoisted_5 = {
  key: 0,
  class: "vanish-circle",
  width: "22px",
  height: "22px",
  viewBox: "0 0 20 20"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("circle", {
  cx: "10",
  cy: "10",
  r: "9"
}, null, -1)
const _hoisted_7 = {
  key: 0,
  class: "custom-toast-body",
  "data-cy": "custom-toast-body"
}
const _hoisted_8 = {
  key: 0,
  class: "custom-toast-body-icon-area"
}
const _hoisted_9 = /*#__PURE__*/_createElementVNode("svg", {
  class: "icon-background",
  width: "78",
  height: "126",
  viewBox: "0 0 78 126",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, [
  /*#__PURE__*/_createElementVNode("path", {
    d: "M57.5 0H0V126H27L78 75L57.5 0Z",
    fill: "#F1F1F1"
  })
], -1)
const _hoisted_10 = { class: "icon-illustration" }
const _hoisted_11 = {
  key: 0,
  class: "icon-illustration-status"
}
const _hoisted_12 = { class: "custom-toast-body-text-area" }
const _hoisted_13 = {
  key: 0,
  class: "text-area-actions"
}

import { createContainer, removeEmptyContainer } from "@common/composable/toast";
import { ColorVariants } from "@medapro-types/common/common";
import { ToastOptions, ToastPositions, ToastProps } from "@medapro-types/common/components/Toast";
import emitter from "tiny-emitter/instance";
import { VNode, computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch, withDefaults } from "vue";
import Icon from "./Icon.vue";
//#region General
type ViewModel = {
  autoCloseTimeInSec: number;
  timeRemainingInMS: number;
  pausedAutoClose: boolean;
  show: boolean;
  autoClose: boolean;
  text: string | VNode | Array<VNode | string>;
  title: string;
  titleIcon: string | undefined;
  textIllustration?: string | undefined;
  statusTextIcon?: string | undefined;
  variant: ColorVariants;
  position: ToastPositions;
  primaryActionButton: string | undefined;
  secondaryActionButton: string | undefined;
  primaryAction: undefined | (() => void);
  secondaryAction: undefined | (() => void);
  id: string | undefined;
  textIsVNode: boolean;
  snackBar: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Toast',
  props: {
    value: { type: Boolean },
    autoClose: { type: Boolean, default: true },
    autoCloseTimeInSec: { default: 5 },
    text: { default: "Text" },
    title: { default: "Title" },
    titleIcon: {},
    textIllustration: {},
    statusTextIcon: {},
    variant: { default: "primary" },
    position: {},
    primaryActionButton: {},
    secondaryActionButton: {},
    id: {},
    snackBar: { type: Boolean, default: false },
    bodyVariant: {}
  },
  emits: ["primaryAction", "secondaryAction", "input"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const toast = ref<HTMLElement | null>(null);
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  timeRemainingInMS: props.autoCloseTimeInSec * 1000,
  autoCloseTimeInSec: props.autoCloseTimeInSec * 1000,
  pausedAutoClose: false,
  show: props.value,
  autoClose: props.autoClose,
  text: props.text,
  title: props.title,
  titleIcon: props.titleIcon,
  textIllustration: props.textIllustration,
  statusTextIcon: props.statusTextIcon,
  variant: props.variant,
  position: props.position ?? (props.snackBar ? "bottom" : "top-right"),
  primaryActionButton: props.primaryActionButton,
  secondaryActionButton: props.secondaryActionButton,
  primaryAction: undefined,
  secondaryAction: undefined,
  id: props.id,
  textIsVNode: false,
  snackBar: props.snackBar
});
//#endregion

//#region Show

const renderedText = () => {
  return vm.text;
};
const renderedTitle = () => {
  return vm.title;
};

const showFunction = (toastOptions: ToastOptions) => {
  Object.assign(vm, toastOptions);
  createContainer(vm.position);
  vm.show = true;
  if (vm.autoClose) triggerClosingAfterSeconds(vm.autoCloseTimeInSec);
};

async function close() {
  removeAllIntervals();
  vm.timeRemainingInMS = vm.autoCloseTimeInSec;
  vm.show = false;
  await nextTick();
  removeEmptyContainer(vm.position);
}

const closeById = async (args: { id: string }) => {
  if (args.id == vm.id) {
    close();
  }
};

const getMilliSecondfromTimeString = (s: string): number | undefined => {
  const regExp = new RegExp("(\\d+)(\\w+)");
  const result = s.match(regExp);
  if (!result) return;
  let duration = parseInt(result[1]);
  switch (result[2]) {
    case "s":
      duration *= 1000;
      break;
    default:
      break;
  }
  return duration;
};

const addAnimationClass = () => {
  const container = createContainer(vm.position);
  if (container && toast.value) {
    container.classList.remove("anim-stop");
    container.classList.add("anim-start");
    const animationLength = getMilliSecondfromTimeString(getComputedStyle(toast.value).animationDuration) ?? 0;
    setTimeout(() => {
      container.classList.remove("anim-start");
      container.classList.add("anim-stop");
    }, animationLength);
  }
};
//#endregion

//#region AutoClose

let timeHelperEnd: number;
const intervals: NodeJS.Timeout[] = [];

const triggerClosingAfterSeconds = (milliseconds: number) => {
  timeHelperEnd = Date.now() + milliseconds;
  vm.timeRemainingInMS = milliseconds;
  intervals.push(
    setInterval(() => {
      vm.timeRemainingInMS = timeHelperEnd - Date.now();
      timeHelperEnd = Date.now() + vm.timeRemainingInMS;
      if (vm.timeRemainingInMS <= 0) {
        timeHelperEnd = 0;
        close();
      }
    }, 50)
  );
};

const pauseAutoClose = () => {
  if (vm.autoClose) {
    removeAllIntervals();
  }
};

const playAutoClose = () => {
  if (vm.autoClose) {
    if (!intervals?.length) {
      triggerClosingAfterSeconds(vm.timeRemainingInMS);
    }
  }
};

const removeAllIntervals = () => {
  while (intervals.length) {
    clearInterval(intervals.pop());
  }
};

//#endregion

//#region Actions
const showActiopnArea = computed(() => {
  return !!vm.primaryActionButton || !!vm.secondaryActionButton;
});

const primaryActionClick = () => {
  if (vm.primaryAction) {
    vm.primaryAction();
  } else {
    emit("primaryAction");
  }
};
const secondaryActionClick = () => {
  if (vm.secondaryAction) {
    vm.secondaryAction();
  } else {
    emit("secondaryAction");
  }
};
//#endregion

//#region LifeCycle

watch(() => toast.value, addAnimationClass);

watch(
  () => props.value,
  async (nv) => {
    if (nv) await showFunction(vm);
    else close();
  }
);

watch(
  () => vm.show,
  (nv) => {
    emit("input", nv);
  },
  { immediate: true }
);

onMounted(() => {
  emitter.on("close-toast-by-id", closeById);
});

onBeforeUnmount(() => {
  emitter.off("close-toast-by-id", closeById);
  removeAllIntervals();
});

//#endregion

//#region Only for Expose

const getId = () => {
  return vm.id;
};

const getPosition = () => {
  return vm.position;
};
//#endregion

__expose({ show: showFunction, close, getId, IsClosed: !vm.show, getPosition });

return (_ctx: any,_cache: any) => {
  return (vm.show)
    ? (_openBlock(), _createBlock(_Teleport, {
        key: 0,
        to: `.toast-content-container.${vm.position}`
      }, [
        _createElementVNode("div", {
          id: vm.id,
          class: _normalizeClass(["custom-toast", [vm.variant, { 'snack-bar': vm.snackBar }]]),
          onMouseover: pauseAutoClose,
          onMouseleave: playAutoClose,
          ref_key: "toast",
          ref: toast,
          "data-cy": "toast"
        }, [
          _renderSlot(_ctx.$slots, "custom-toast-header", {}, () => [
            _createElementVNode("div", _hoisted_2, [
              (vm.titleIcon)
                ? (_openBlock(), _createBlock(Icon, {
                    key: 0,
                    icon: vm.titleIcon
                  }, null, 8, ["icon"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(renderedTitle)
                ])
              ]),
              _createElementVNode("div", {
                class: "custom-toast-close",
                "data-cy": "toast-close-btn",
                onClick: close
              }, [
                _createVNode(Icon, { icon: "Close" }),
                (vm.autoClose)
                  ? (_openBlock(), _createElementBlock("svg", _hoisted_5, [
                      _hoisted_6,
                      _createElementVNode("path", {
                        d: "M10,1 L10,1 L10,1 C14.9705627,1 19,5.02943725 19,10 L19,10 L19,10 C19,14.9705627 14.9705627,19 10,19 L10,19 L10,19 C5.02943725,19 1,14.9705627 1,10 L1,10 L1,10 C1,5.02943725 5.02943725,1 10,1 L10,1 Z",
                        class: "outer",
                        style: _normalizeStyle({ 'stroke-dashoffset': 57 - 57 * (vm.timeRemainingInMS / vm.autoCloseTimeInSec) })
                      }, null, 4)
                    ]))
                  : _createCommentVNode("", true)
              ])
            ])
          ]),
          (!vm.snackBar)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _renderSlot(_ctx.$slots, "default", {}, () => [
                  (vm.textIllustration)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _hoisted_9,
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(Icon, {
                            icon: vm.textIllustration,
                            size: "32px"
                          }, null, 8, ["icon"]),
                          (vm.statusTextIcon)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createVNode(Icon, {
                                  icon: vm.statusTextIcon,
                                  variant: vm.variant
                                }, null, 8, ["icon", "variant"])
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["text-area-content", { 'with-button-area': showActiopnArea.value }])
                    }, [
                      _createVNode(renderedText)
                    ], 2),
                    (showActiopnArea.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _renderSlot(_ctx.$slots, "primaryAction", {}, () => [
                            (vm.primaryActionButton)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  class: "btn mono",
                                  "data-cy": "toast-primary-action-btn",
                                  onClick: primaryActionClick
                                }, _toDisplayString(vm.primaryActionButton), 1))
                              : _createCommentVNode("", true)
                          ]),
                          _renderSlot(_ctx.$slots, "primaryAction", {}, () => [
                            (vm.secondaryActionButton)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  class: "btn mono",
                                  "data-cy": "toast-secondary-action-btn",
                                  onClick: secondaryActionClick
                                }, _toDisplayString(vm.secondaryActionButton), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ], 42, _hoisted_1)
      ], 8, ["to"]))
    : _createCommentVNode("", true)
}
}

})